/**
 * VRG-427 — "return the user to their previous route" when a page crashes.
 *
 * Tracks the last route that rendered WITHOUT throwing. This works precisely
 * because of how React commits an error: `useTrackRouteForRecovery`'s effect
 * only runs AFTER a commit succeeds. If navigating to a route makes it crash,
 * React discards that whole commit and unwinds to the nearest error boundary
 * without ever running pending effects for the aborted tree — so the effect for
 * the CRASHING route never fires, and the value recorded here is still the last
 * route that actually rendered. That is exactly the "previous page" the product
 * decision asks for, with no extra bookkeeping required.
 *
 * Module-level rather than a store/context: `ErrorBoundary` is a class
 * component with no hook access, and the value must be readable synchronously
 * from `componentDidCatch`, outside of any render.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { ROUTES } from 'routes/routes'

let lastKnownGoodRoute: string | null = null

/*
  VRG-721 — the page BEFORE the current one, for a support report's context.

  Pathname only (no query string — that is where search terms and emails live),
  and the intake route is never recorded: it is a modal over the page being
  reported, so counting it would make "previous" mean "the page you were on"
  rather than "the page before it". `currentPage` is the page behind the intake;
  `previousPage` is the one before that, which is what a reproduction needs — the
  bug is often in how you ARRIVED.
*/
let currentPage: string | null = null
let previousPage: string | null = null

/** The page visited before the current one (pathname only), or null on the first page. */
export const getPreviousRoute = (): string | null => previousPage

/** The last route that committed successfully, or null if none has yet (first paint crashed). */
export const getLastKnownGoodRoute = (): string | null => lastKnownGoodRoute

/**
 * Mount ONCE, near the app root, inside the router. Recording happens in an
 * effect (post-commit) rather than during render, which is the property this
 * whole mechanism depends on — see the file header.
 */
export const useTrackRouteForRecovery = (): void => {
  const location = useLocation()
  useEffect(() => {
    lastKnownGoodRoute = `${location.pathname}${location.search}`
    if (location.pathname !== ROUTES.SUPPORT_NEW && location.pathname !== currentPage) {
      previousPage = currentPage
      currentPage = location.pathname
    }
  }, [location.pathname, location.search])
}
